import { useMemo } from "react";
import { useWorkflows } from "./useWorkflows";
import { useWorkflowProgress } from "./useWorkflowProgress";
import { useAppSelector } from "../../../redux/hooks";
import { sqprStillImporting } from "../../workflow-progress-v2/sqprSettlement";
import type { Workflow } from "../../../zodios/apiDefinitions/workflows";

const RECENT_POLL_MS = 1000 * 15;
const BACKFILL_POLL_MS = 1000 * 60 * 2.5;
// Keep showing completed progress for a day after the most recent workflow finished, then hide.
// Non-staff also see a failed run for this long (softened to "taking a little longer") before it
// clears; staff keep seeing failures until they're resolved internally.
const HIDE_AFTER_MS = 1000 * 60 * 60 * 24;

const hasFailed = (workflow: Workflow) => workflow.status === "FAILED" || workflow.status === "CANCELLED";

const latestByPrefix = (workflows: Workflow[] | undefined, prefix: string) =>
  workflows
    ?.filter((workflow) => workflow.id.startsWith(prefix))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

export const useOnboardingProgress = () => {
  const isStaff = useAppSelector((state) => state.user.is_staff);
  const workflowsQuery = useWorkflows({ page_size: 100 }, { refetchInterval: RECENT_POLL_MS });

  const recentWorkflow = useMemo(() => latestByPrefix(workflowsQuery.data, "onboarding_"), [workflowsQuery.data]);
  const sellerAnalyticsWorkflow = useMemo(
    () => latestByPrefix(workflowsQuery.data, "seller_analytics_"),
    [workflowsQuery.data],
  );

  const recent = useWorkflowProgress(recentWorkflow?.tracking_id, { refetchInterval: RECENT_POLL_MS });

  // Only treat a `backfill_` workflow as belonging to the current run if it was created after
  // the recent workflow. Otherwise the newest backfill by prefix can be a completed one from a
  // previous run — surfacing the new run's backfill as "completed" while recent is still pending.
  const backfillWorkflow = useMemo(() => {
    const latest = latestByPrefix(workflowsQuery.data, "backfill_");
    if (!latest || !recentWorkflow) return latest;
    const isStale = new Date(latest.created_at).getTime() < new Date(recentWorkflow.created_at).getTime();
    return isStale ? undefined : latest;
  }, [workflowsQuery.data, recentWorkflow]);

  // Fall back to the id linked on the recent progress when the workflow isn't in the list yet.
  const backfillTrackingId = backfillWorkflow?.tracking_id ?? recent.data?.backfill_workflow_id ?? undefined;
  const backfill = useWorkflowProgress(backfillTrackingId, {
    refetchInterval: BACKFILL_POLL_MS,
    keepPollingWhenFinished: (data) => sqprStillImporting(data.sqpr_progress, data.sqpr_dispatched_at),
  });

  const sqprImporting = sqprStillImporting(backfill.data?.sqpr_progress, backfill.data?.sqpr_dispatched_at);

  const shouldShow = useMemo(() => {
    if (sqprImporting) return true;
    const cohort = [recentWorkflow, backfillWorkflow].filter((workflow): workflow is Workflow => !!workflow);
    if (!cohort.length) return false;

    const withinWindow = () => {
      const lastUpdatedAt = Math.max(...cohort.map((workflow) => new Date(workflow.updated_at).getTime()));
      return Date.now() - lastUpdatedAt < HIDE_AFTER_MS;
    };

    // Staff resolve failures internally, so keep the UI up while anything is unfinished OR failed;
    // only a fully successful cohort clears (after the usual window).
    if (isStaff) {
      if (cohort.some((workflow) => !workflow.is_finished || hasFailed(workflow))) return true;
      return withinWindow();
    }

    // Non-staff: keep showing while genuinely progressing. A failed run isn't "active" — it's
    // softened to "taking a little longer" and lingers only for the window (a 24h buffer), then
    // clears on its own rather than spinning forever.
    if (cohort.some((workflow) => !workflow.is_finished && !hasFailed(workflow))) return true;
    return withinWindow();
  }, [recentWorkflow, backfillWorkflow, isStaff, sqprImporting]);

  const isLoading = workflowsQuery.isLoading || recent.isLoading || backfill.isLoading;

  return { recentWorkflow, backfillWorkflow, sellerAnalyticsWorkflow, recent, backfill, shouldShow, isLoading };
};
